'use client'

import textHeadingL from '@fukku/styles/texts/textHeadingL.module.css'
import textHeadingM from '@fukku/styles/texts/textHeadingM.module.css'
import { cx } from 'classix'
import { type ReactNode, useRef } from 'react'
import {
	type Cta,
	type HeroBannerPromo,
	HeroBannerPromoType,
} from '../../../../Home.type'
import stylesBGColor from '../../PromotionsBackgroundColors.module.css'
import focusStyle from '../../Shared/A11y/TabFocus.module.css'
import { ButtonCtas } from '../../Shared/ctas/ButtonCtas/ButtonCtas'
import { useActiveLink } from '../../Shared/ctas/hooks/onActiveLink'
import { InfoTooltip } from '../../Shared/InfoTooltip/InfoTooltip'
import styles from './HeroBannerPromo.module.css'
import { HeroBannerPromoCountdown } from './HeroBannerPromoCountdown'
import { HeroBannerPromoFamily } from './HeroBannerPromoFamily'
import { HeroBannerPromoGeneral } from './HeroBannerPromoGeneral'
import { HeroBannerPromoImage } from './HeroBannerPromoImage'
import { HeroBannerPromoProgressive } from './HeroBannerPromoProgressive'

type HeroBannerPromoContentProps = {
	readonly section: HeroBannerPromo
	readonly ctas: Cta[]
	readonly infoLabel: string
	readonly onlineExclusiveLabel: string
	readonly isMobile: boolean
	readonly isPrioritySlot: boolean | undefined
}

type HeroBannerPromoTypeProps = {
	readonly section: HeroBannerPromo
}

type HeroBannerPromoSubcomponent =
	| typeof HeroBannerPromoGeneral
	| typeof HeroBannerPromoProgressive
	| typeof HeroBannerPromoFamily

function HeroBannerPromoTypeComponent({ section }: HeroBannerPromoTypeProps) {
	const promoMap: Record<HeroBannerPromoType, HeroBannerPromoSubcomponent> = {
		[HeroBannerPromoType.general]: HeroBannerPromoGeneral,
		[HeroBannerPromoType.progressive]: HeroBannerPromoProgressive,
		[HeroBannerPromoType.family]: HeroBannerPromoFamily,
	}

	const PromoComponent = promoMap[section.promoType]

	return <PromoComponent section={section} />
}

export function HeroBannerPromoContent({
	section,
	ctas,
	infoLabel,
	onlineExclusiveLabel,
	isMobile,
	isPrioritySlot,
}: HeroBannerPromoContentProps): ReactNode {
	const {
		image,
		countdown,
		promoBackgroundColor,
		promoLegalInfo,
		promoSubtitles: { condition, onlineExclusive, dueDate, promoCode },
	} = section

	const ref = useRef<HTMLDivElement>(null)
	const activeLink = useActiveLink(ref)

	return (
		<div className={styles.bannerWrapper} ref={ref}>
			<div className={styles.layout}>
				<div className={styles.imageWrapper}>
					<HeroBannerPromoImage image={image} priority={isPrioritySlot} />
				</div>
				<div
					className={cx(styles.solidColor, stylesBGColor[promoBackgroundColor])}
				/>
			</div>
			<div className={styles.layout}>
				<div className={styles.promoGeneral}>
					<div className={styles.promo}>
						<div className={cx(styles.counter, textHeadingL.className)}>
							<HeroBannerPromoCountdown date={countdown} />
						</div>
						<HeroBannerPromoTypeComponent section={section} />
						<h3 className={cx(styles.subtitles, textHeadingM.className)}>
							<div>
								{[condition, onlineExclusive && onlineExclusiveLabel]
									.filter(Boolean)
									.join(' | ')}
							</div>
							<div>{[dueDate, promoCode].filter(Boolean).join(' | ')}</div>
						</h3>
					</div>
					<ButtonCtas
						className={cx(styles.ctas, focusStyle.imageFocus)}
						ctas={ctas}
						activeLink={activeLink}
						data-testid='landings.home.heroBannerPromoLink'
					/>
					<InfoTooltip
						className={styles.infoTooltip}
						title={infoLabel}
						showInfo={Boolean(promoLegalInfo)}
						infoText={promoLegalInfo}
						position={isMobile ? 'bottom' : 'left'}
						inverse
					/>
				</div>
			</div>
		</div>
	)
}
